<template>
  <div>
    <TourCityCoverBlock
      v-if="isLocation.isCity"
      :current-tag="filtersStore.state.tag__slugs || ''"
      :slug="props.slug"
      :geo-object="geoObject"
      :tags="cityTags ?? []"
    />

    <TourRegionCoverBlock
      v-else-if="!isCountryTour"
      :from-city-seo-payload="fromCitySeoPayload"
      :current-tag="filtersStore.state.tag__slugs || ''"
      :page="props.page"
      :slug="props.slug"
      :geo-object="geoObject"
      :tags
      :location
    />

    <TourRegionsInCountryCoverBlock
      v-else
      :current-tag="filtersStore.state.tag__slugs || ''"
      :page="props.page"
      :fromCitySeoPayload="props.fromCitySeoPayload"
      :slug="props.slug"
      :geo-object="geoObject"
      :tags
    />

    <TourFiltersBlock
      :currency-code="props.currencyCode"
      :is-region="isLocation.isRegion"
      :page="page"
      :default-sogu-fields="props.defaultSoguFields"
      :slug="props.slug"
      :location
      @updated="emit('filtersUpdated')"
    />

    <TourRubricsBlock
      :key="props.location"
      :slug="props.slug"
      :location
      :default-sogu-fields="props.defaultSoguFields"
      :region-tags="regionTags"
      :country-tags="countryTags"
      :city-tags="cityTags"
      @tagChanged="emit('tagChanged', $event)"
    />

    <TourSmallOutputBlock
      :key="props.currencyCode"
      :tags
      :currency-code="props.currencyCode"
    />
  </div>
</template>

<script setup lang="ts">
  import { TourRubricsBlock } from '@/widgets/rubricsBlock/tour';
  import { TourFiltersBlock } from '@/widgets/filtersBlock/tour';
  import { TourSmallOutputBlock } from '@/widgets/smallOutputBlock/tour';
  import { TourCityCoverBlock, TourRegionCoverBlock, TourRegionsInCountryCoverBlock } from '@/widgets/coverBlock/tour';
  import { type FromCityPayload, LocationType } from '@/entities/location';
  import type { CityInfo, CityTag, CountryInfo, CountryTag, RegionInfo, RegionTag } from '@/shared/api/experience';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import type { ContextGeoSubject } from '@/shared/types/geography';
  import { computed } from 'vue';
  import { useRegionTag } from '@/entities/tag/api/useRegionTag.ts';
  import { useCountryTag } from '@/entities/tag/api/useCountryTag.ts';
  import { useCityTag } from '@/entities/tag/api/useCityTag.ts';
  import type { CurrencyCode } from '@/shared/lib/currency';
  import type { ExtendedPartTagType } from '@/entities/rubrics';

  interface Props {
    slug: string;
    page: number;
    location: LocationType;
    fromCitySeoPayload?: Nullable<FromCityPayload>;
    geoObject: RegionInfo | CountryInfo | CityInfo;
    defaultSoguFields: ContextGeoSubject;
    currencyCode: CurrencyCode;
  }

  const props = withDefaults(defineProps<Props>(), {});

  const emit = defineEmits<{
    filtersUpdated: [];
    tagChanged: [tag: ExtendedPartTagType];
  }>();

  const filtersStore = useTourFiltersStore();

  const { regionTags } = useRegionTag({
    slug: computed(() => props.slug),
    enabled: computed(() => props.location === LocationType.REGION),
  });

  const { countryTags } = useCountryTag({
    slug: computed(() => props.slug),
    enabled: computed(() => props.location === LocationType.COUNTRY),
  });

  const { cityTags } = useCityTag({
    slug: computed(() => props.slug),
    sorting: computed(() => '-popularity' as const),
    enabled: computed(() => props.location === LocationType.CITY),
  });

  const isLocation = computed(() => ({
    isRegion: props.location === LocationType.REGION,
    isCountry: props.location === LocationType.COUNTRY,
    isCity: props.location === LocationType.CITY,
  }));

  const isCountryTour = computed(() => props.location === LocationType.COUNTRY);

  const tags = computed<RegionTag[] | CountryTag[] | CityTag[]>(() => {
    if (props.location === LocationType.REGION) {
      return regionTags.value ?? [];
    }
    if (props.location === LocationType.COUNTRY) {
      return countryTags.value ?? [];
    }
    if (props.location === LocationType.CITY) {
      return cityTags.value ?? [];
    }

    return [];
  });

  // Обработка 404 для /experience/Saint_Petersburg/not-existing-rubric-1234/
  // watchOnce(
  //   () => tags.value,
  //   () => {
  //     const { tag } = route.params;

  //     if (!tag) {
  //       return;
  //     }

  //     const isTagExist = tags.value.some((tagItem) => tag.includes(tagItem.slug as string));

  //     loaderStore.is404 = !isTagExist;
  //   },
  // );

  watchEffect(() => {
    if (isLocation.value.isRegion) {
      filtersStore.pageParams.region = props.slug;
      filtersStore.pageParams.city = undefined;
      filtersStore.pageParams.country = undefined;
    }

    if (isLocation.value.isCountry) {
      filtersStore.pageParams.country = props.slug;
      filtersStore.pageParams.city = undefined;
      filtersStore.pageParams.region = undefined;
    }

    if (props.location === LocationType.CITY) {
      filtersStore.pageParams.city = props.slug;
      filtersStore.pageParams.region = undefined;
      filtersStore.pageParams.country = undefined;
    }
  });
</script>
